/**
 * ContactSection -- 연락처 섹션 편집 (본인 + 부모님)
 */

import { useState } from 'react'
import { Phone } from 'lucide-react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { Input } from '../../ui/index.js'
import styles from './SectionForm.module.css'

type PersonType = 'groom' | 'bride'

/** 숫자만 추출 후 전화번호 형식으로 포맷 (010-1234-5678) */
function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11)

  if (digits.length <= 3) return digits
  if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`
}

/** 전화번호 유효성 검사 (빈 값은 유효) */
function validatePhone(value: string): string | undefined {
  if (!value) return undefined
  const digits = value.replace(/\D/g, '')
  if (digits.length > 0 && digits.length < 10) return '전화번호를 확인해주세요'
  if (digits.length > 0 && !/^0\d{9,10}$/.test(digits)) return '올바른 전화번호 형식이 아닙니다'
  return undefined
}

interface PhoneInputProps {
  label: string
  value: string
  onChange: (value: string) => void
}

function PhoneInput({ label, value, onChange }: PhoneInputProps) {
  const [touched, setTouched] = useState(false)
  const error = touched ? validatePhone(value) : undefined

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value)
    onChange(formatted)
  }

  return (
    <Input
      label={label}
      type="tel"
      value={value}
      onChange={handleChange}
      onBlur={() => setTouched(true)}
      placeholder="010-0000-0000"
      error={error}
    />
  )
}

interface ContactCardProps {
  type: PersonType
  person: Record<string, unknown> | undefined
  updateNested: (path: string, value: unknown) => void
}

function ContactCard({ type, person, updateNested }: ContactCardProps) {
  const label = type === 'groom' ? '신랑' : '신부'

  return (
    <div className={styles.personCard} data-person={type}>
      <div className={styles.personCardHeader}>
        <span className={styles.personCardIconBadge}>
          <Phone size={20} />
        </span>
        <span className={styles.personCardLabel}>{label} 측</span>
      </div>
      <div className={styles.personCardDivider} />
      <div className={styles.personCardBody}>
        <PhoneInput
          label={`${label} 연락처`}
          value={(person?.phone as string) ?? ''}
          onChange={(v) => updateNested(`${type}.phone`, v)}
        />

        <div className={styles.subSection}>
          <span className={styles.subSectionTitle}>부모님 연락처</span>
          <div className={styles.row}>
            <PhoneInput
              label="아버지"
              value={(person?.fatherPhone as string) ?? ''}
              onChange={(v) => updateNested(`${type}.fatherPhone`, v)}
            />
            <PhoneInput
              label="어머니"
              value={(person?.motherPhone as string) ?? ''}
              onChange={(v) => updateNested(`${type}.motherPhone`, v)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export function ContactSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  if (!invitation) return null

  return (
    <div className={styles.personCardRow}>
      <ContactCard
        type="groom"
        person={invitation.groom as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
      <ContactCard
        type="bride"
        person={invitation.bride as Record<string, unknown> | undefined}
        updateNested={updateNested}
      />
    </div>
  )
}
